.Button {
  appearance: none;
  user-select: none;
  vertical-align: middle;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, border-color 0.2s;
  text-decoration: none;
  border-radius: var(--base-size-6);
  cursor: pointer;
  border: solid var(--brand-borderWidth-thin) transparent;
  background: none;
  box-sizing: border-box;
  display: inline-flex;
  gap: var(--brand-control-medium-gap);
}

.Button:where(button) {
  font: inherit;
}

.Button:hover {
  text-decoration: none;
}

.Button:focus-visible {
  transition: none;
  outline: 4px solid var(--brand-color-focus);
  outline-offset: 2px;
}

.Button--disabled,
.Button--disabled:hover,
.Button--disabled:focus {
  cursor: not-allowed;
}

.Button--size-small .Button__text {
  display: flex;
}

.Button__text {
  position: relative;
}

.Button--label {
  font-family: var(--brand-body-fontFamily);
  font-style: normal;
  font-weight: var(--base-text-weight-medium);
  text-align: center;
}

.Button__icon-visual {
  display: flex;
  align-self: center;
  height: var(--brand-Button-icon-size-medium);
  width: auto;
  fill: currentcolor;
}

.Button__trailing-visual,
.Button__leading-visual {
  display: flex;
}

/* optically adjust arrow to align with the end (right) padding */
/* reduce gap between label and arrow */
.Button-arrow {
  margin-inline-end: calc(var(--base-size-4) * -1);
  margin-inline-start: calc(var(--base-size-8) * -1);
}

/* primary */

.Button--primary {
  color: var(--brand-button-primary-fgColor-rest);
  background-color: var(--brand-button-primary-bgColor-rest);
}

.Button--size-small.Button--primary {
  border-width: var(--brand-borderWidth-thin);
}

.Button--primary:where(:not(.Button[disabled], .Button[aria-disabled='true'], [aria-expanded='true'])):hover {
  background-color: var(--brand-button-primary-bgColor-hover);
}

.Button--primary:where(:not(.Button[disabled], .Button[aria-disabled='true'])):active,
.Button--primary[aria-expanded='true'] {
  background: var(--brand-button-primary-bgColor-active);
  border-color: var(--brand-button-primary-borderColor-active);
}

.Button--label-primary {
  color: var(--brand-button-primary-fgColor-rest);
}

.Button--primary:disabled,
.Button--primary[aria-disabled='true'],
.Button--primary.Button--disabled {
  background-color: var(--brand-button-primary-bgColor-disabled);
}

.Button--primary:where(:disabled, [aria-disabled='true'], .Button--disabled)
  :where(.Button-arrow--disabled, .Button__icon-visual--disabled),
.Button--label-primary.Button-label--disabled {
  color: var(--brand-button-primary-fgColor-disabled);
}

/* secondary */

.Button--secondary {
  background-color: var(--brand-button-secondary-bgColor-rest);
  border-color: var(--brand-button-secondary-borderColor-rest);
  color: var(--brand-button-secondary-fgColor-rest);
  backdrop-filter: blur(var(--base-size-20));
}

.Button--secondary:where(:not(.Button[disabled], .Button[aria-disabled='true'], [aria-expanded='true'])):hover {
  background-color: var(--brand-button-secondary-bgColor-hover);
  border-color: var(--brand-button-secondary-borderColor-hover);
}

.Button--secondary:where(:not(.Button[disabled], .Button[aria-disabled='true'])):active,
.Button--secondary[aria-expanded='true'] {
  background-color: var(--brand-button-secondary-bgColor-active);
  border-color: var(--brand-button-secondary-borderColor-active);
}

.Button--secondary:disabled {
  border-color: var(--brand-button-secondary-fgColor-disabled);
}

.Button--secondary:where(:disabled, [aria-disabled='true'], .Button--disabled)
  :where(.Button-arrow--disabled, .Button__icon-visual--disabled),
.Button--label-secondary.Button-label--disabled {
  color: var(--brand-button-secondary-fgColor-disabled);
}

/* subtle */

.Button--subtle {
  background-color: var(--brand-button-subtle-bgColor-rest);
  border-width: var(--brand-borderWidth-thin);
  border-color: var(--brand-button-subtle-borderColor-rest);
  color: var(--brand-color-text-default);
  backdrop-filter: blur(var(--base-size-20));
}

.Button--subtle:where(:not(.Button[disabled], .Button[aria-disabled='true'], [aria-expanded='true'])):hover {
  background-color: var(--brand-button-subtle-bgColor-hover);
  border-color: var(--brand-button-subtle-borderColor-hover);
}

.Button--subtle:where(:not(.Button[disabled], .Button[aria-disabled='true'])):active,
.Button--subtle[aria-expanded='true'] {
  background-color: var(--brand-button-subtle-bgColor-active);
  border-color: var(--brand-button-subtle-borderColor-active);
}

.Button--label-subtle {
  color: var(--brand-button-subtle-fgColor-rest);
}

.Button--subtle:where(:disabled, [aria-disabled='true'], .Button--disabled)
  :where(.Button-arrow--disabled, .Button__icon-visual--disabled),
.Button--label-subtle.Button-label--disabled {
  color: var(--brand-button-subtle-fgColor-disabled);
}

/* size */

.Button--size-small {
  min-height: var(--brand-Button-control-size-small);
  padding-inline: var(--brand-control-small-paddingInline-normal);
}

.Button--size-medium {
  min-height: var(--brand-Button-control-size-medium);
  padding: var(--brand-control-medium-paddingBlock-condensed) var(--base-size-20);
}

.Button--size-large {
  min-height: var(--brand-Button-control-size-large);
  padding: var(--brand-control-large-paddingBlock-condensed) var(--brand-control-large-paddingInline-spacious);
}

/* Keep visual sizing overridable by composed controls such as IconButton. */
.Button--size-small :where(.Button__icon-visual) {
  height: var(--brand-Button-icon-size-small);
}

.Button--size-medium :where(.Button__icon-visual) {
  height: var(--brand-Button-icon-size-medium);
}

.Button--size-large :where(.Button__icon-visual, .Button-arrow) {
  height: var(--brand-Button-icon-size-large);
  width: var(--brand-Button-icon-size-large);
}

.Button--label-medium {
  font-size: var(--brand-text-size-200);
  line-height: var(--brand-text-lineHeight-200);
  letter-spacing: 0.01rem;
}

.Button--label-large {
  font-size: var(--brand-text-subhead-size-large);
  line-height: var(--brand-text-subhead-lineHeight-large);
}

.Button--block {
  width: 100%;
}

@media (forced-colors: active) {
  .Button--disabled {
    outline-color: GrayText;
  }

  /* Indicates disabled state in WHCM */
  .Button--label.Button-label--disabled {
    color: GrayText;
  }
}

/* Remove all animations, transitions and smooth scroll for people that prefer not to see them */
@media (prefers-reduced-motion: reduce) {
  .Button,
  .Button::before,
  .Button::after,
  .Button * {
    animation: none !important;
    transition: none !important;
    scroll-behavior: auto !important;
  }
}
